/**
 * Custom SideNavbarComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { SideNavbarComponent as ParentSideNavbarComponent } from '@shared-lib/chart-components/side-navbar/side-navbar.component';
import { environment } from '../../../projects/my-app/src/environments/environment';
import { ReportsNavigationService } from '../../shared-services/reports-navigation.service';

@Component({
  selector: 'app-side-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatTooltipModule, MatButtonModule],
  templateUrl: './side-navbar.component.html',
  styleUrls: ['./side-navbar.component.scss']
})
export class SideNavbarComponent extends ParentSideNavbarComponent {
  private static readonly PRICE_FILE_GENERATOR_ROUTE = '/regional';

  private static readonly HOME_ROUTES: Record<string, string> = {
    'Data Explorer': '/spriced-data',
    'System Administration': '/spriced-data-definition',
    'System Admin': '/spriced-data-definition',
  };

  private readonly reportsNavigation = inject(ReportsNavigationService);

  private getReportsDestination() {
    const environmentConfig = environment as typeof environment & {
      HOME_URL?: string;
    };

    return this.reportsNavigation.getDestination(
      window.location.hostname,
      environmentConfig.URL,
      environmentConfig.HOME_URL,
    );
  }

  selectedSidebarItemName = '';
  showProfileMenu = false;

  toggleProfileMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.showProfileMenu = !this.showProfileMenu;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.showProfileMenu = false;
  }

  /** System Admin sub-headers — rendered in the top bar (in place of the
   *  default Model View / Explorer / Notifications tabs) while the user is on
   *  any /administration route. Each links to its own sub-page. Ids must match
   *  the section ids the SystemAdminComponent resolves from the route. */
  private readonly ADMIN_TOPBAR_ITEMS = [
    { name: 'Model Management', path: '/administration/model-management' },
    { name: 'Entity Management', path: '/administration/entity-management' },
    { name: 'Business Rules', path: '/administration/business-rules' },
    { name: 'Derived Hierarchy', path: '/administration/derived-hierarchy' },
    { name: 'Functions Permission', path: '/administration/functions-permission' },
    { name: 'Model Permission', path: '/administration/model-permission' },
    { name: 'Hierarchy Permission', path: '/administration/hierarchy-permission' },
    { name: 'Transactions', path: '/administration/transactions' },
    { name: 'Settings', path: '/administration/settings' },
  ];

  get topbarItems(): any[] {
    const currentPath = String(this.router.url || '').split(/[?#]/)[0].replace(/\/$/, '');

    // These Price File Generator pages use their dashboard title as the sole
    // top-bar label. Keep the standard top-bar items on every other page.
    if (currentPath === '/regional' || currentPath === '/oem') {
      return [];
    }

    // On the Administration page the top bar becomes the System Admin nav.
    if (currentPath === '/administration' || currentPath.startsWith('/administration/')) {
      return this.ADMIN_TOPBAR_ITEMS;
    }
    return (this.menuItems || []).filter((item: any) => item.position === 'topbar');
  }

  get sidebarItems(): any[] {
    return (this.menuItems || []).filter((item: any) => item.position !== 'topbar');
  }

  selectSidebarItem(item: any): void {
    this.selectedSidebarItemName = item?.name || '';
  }

  isSidebarItemSelected(item: any): boolean {
    if (!item?.name) {
      return false;
    }

    const currentPath = String(this.router.url || '').split(/[?#]/)[0].replace(/\/$/, '');
    const routeMatch = this.sidebarItems
      .map((sidebarItem: any) => ({
        item: sidebarItem,
        path: String(sidebarItem?.path || '').split(/[?#]/)[0].replace(/\/$/, ''),
      }))
      .filter(({ path }: { path: string }) =>
        !!path && (currentPath === path || currentPath.startsWith(`${path}/`)),
      )
      // Nested routes can match more than one parent path. The longest path
      // is the most specific destination and must be the only active item.
      .sort((left: { path: string }, right: { path: string }) => right.path.length - left.path.length)[0];

    if (routeMatch) {
      return routeMatch.item === item;
    }

    return this.selectedSidebarItemName === item.name;
  }

  goToHome(): void {
    const homeUrl = (environment as typeof environment & { HOME_URL?: string }).HOME_URL || environment.URL;
    window.location.assign(homeUrl);
  }

  override toggleTheme(): any {
    // TODO: Add your custom logic here
    return super.toggleTheme();
  }

  override saveThemePreview(): any {
    // TODO: Add your custom logic here
    return super.saveThemePreview();
  }

  override toggleSlideshowPlayback(): any {
    // TODO: Add your custom logic here
    return super.toggleSlideshowPlayback();
  }

  override slideshowNext(): any {
    // TODO: Add your custom logic here
    return super.slideshowNext();
  }

  override slideshowPrev(): any {
    // TODO: Add your custom logic here
    return super.slideshowPrev();
  }

  override slideshowApply(): any {
    // TODO: Add your custom logic here
    return super.slideshowApply();
  }

  override slideshowSave(): any {
    // TODO: Add your custom logic here
    return super.slideshowSave();
  }

  override slideshowStop(): any {
    // TODO: Add your custom logic here
    return super.slideshowStop();
  }

  override startSidebarResize(event: any): any {
    // TODO: Add your custom logic here
    return super.startSidebarResize(event);
  }

  protected override endSidebarResize(commit: any): any {
    // TODO: Add your custom logic here
    return super.endSidebarResize(commit);
  }

  protected override clampSidebarWidth(width: any): any {
    // TODO: Add your custom logic here
    return super.clampSidebarWidth(width);
  }

  protected override persistSidebarWidth(): any {
    // TODO: Add your custom logic here
    return super.persistSidebarWidth();
  }

  protected override loadSidebarWidth(): any {
    // TODO: Add your custom logic here
    return super.loadSidebarWidth();
  }

  protected override initLogos(): any {
    // TODO: Add your custom logic here
    return super.initLogos();
  }

  override logout(): any {
    // TODO: Add your custom logic here
    return super.logout();
  }

  override loadSidenavConfig(): any {
    this.http
      .get<any[]>('assets/side-navbar-config/sidenav.config.json')
      .subscribe((data) => {
        this.menuItems = data.map((item) => {
          if (item?.name === 'Price File Generator') {
            const { envUrlKey: _envUrlKey, ...priceFileGeneratorItem } = item;
            return {
              ...priceFileGeneratorItem,
              path: SideNavbarComponent.PRICE_FILE_GENERATOR_ROUTE,
              external: false,
            };
          }

          const homeRoute = SideNavbarComponent.HOME_ROUTES[item?.name];
          return homeRoute
            ? { ...item, path: homeRoute, external: true, envUrlKey: 'HOME_URL' }
            : item;
        });
      });
  }

  override onMenuItemClick(item: any, event: Event): boolean {
    if (item?.name === 'Reports') {
      event.preventDefault();

      // The configured Reports entry is currently an internal Angular route.
      // Preserve the external destination flow for deployments that explicitly
      // mark the same menu item as external.
      if (!item.external) {
        void this.router.navigateByUrl(item.path || '/reports');
        return false;
      }

      const destination = this.getReportsDestination();
      if (destination.external) {
        window.location.assign(destination.path);
      } else {
        void this.router.navigateByUrl(destination.path);
      }

      return false;
    }

    const homeRoute = SideNavbarComponent.HOME_ROUTES[item?.name];
    if (!homeRoute) {
      return super.onMenuItemClick(item, event);
    }

    const homeUrl = (environment as typeof environment & { HOME_URL?: string }).HOME_URL;
    if (!homeUrl) {
      return true;
    }

    event.preventDefault();
    const destination = `${homeUrl.replace(/\/+$/, '')}/${homeRoute.replace(/^\/+/, '')}`;
    window.location.assign(destination);
    return false;
  }

  override toggleSidebar(): any {
    // TODO: Add your custom logic here
    return super.toggleSidebar();
  }

  protected override scheduleDesktopReflow(): any {
    // TODO: Add your custom logic here
    return super.scheduleDesktopReflow();
  }

  override mouseenter(): any {
    // TODO: Add your custom logic here
    return super.mouseenter();
  }

  override mouseleave(): any {
    // TODO: Add your custom logic here
    return super.mouseleave();
  }

  override goToHelp(): any {
    return super.goToHelp();
  }

  override goToFeedback(): any {
    // TODO: Add your custom logic here
    return super.goToFeedback();
  }

  override showNotifications(): any {
    // TODO: Add your custom logic here
    return super.showNotifications();
  }

  override openThemeSelector(): any {
    // TODO: Add your custom logic here
    return super.openThemeSelector();
  }

  override toggleSubmenu(menuName: any, event?: any): any {
    // TODO: Add your custom logic here
    return super.toggleSubmenu(menuName, event);
  }

  override isMenuExpanded(menuName: any): any {
    // TODO: Add your custom logic here
    return super.isMenuExpanded(menuName);
  }

  override isActive(item: any): any {
    // TODO: Add your custom logic here
    return super.isActive(item);
  }

  override canShowMacroShortcut(): any {
    // TODO: Add your custom logic here
    return super.canShowMacroShortcut();
  }

  override toggleMacroPanel(event?: any): any {
    // TODO: Add your custom logic here
    return super.toggleMacroPanel(event);
  }

  override selectMacro(macro: any): any {
    // TODO: Add your custom logic here
    return super.selectMacro(macro);
  }

  override hasChildren(item: any): any {
    // TODO: Add your custom logic here
    return super.hasChildren(item);
  }


}
